
import { GetServerSideProps } from "next";
import VideoPageHead, { IVideoPageHead } from "@/components/Head/video-page";
import { allHeadings, DEEPLINK, DEEPLINK_PREFIX, FRONTEND_HOST, STATIC_CDN } from "@/config/constants";
import { getExcludedStudentIds, getLanguageFromNonNativeLocale, isHindi, isPureHindi } from "@/utils/language";
import { cleanOcrText, toTitleCase, getSolutionTypeText, countUpvotes, getMetaDescription, getHeadTitle, getMetaKeyword, getCurrentDate, isCSRRequest, generateInternalDeeplinkFromBranchDeeplink } from "@/utils";
import dynamic from "next/dynamic";
import MathJax from "@/components/MathJaxText";
import VideoPlayer from "@/components/VideoPlayer";
import Options from "@/components/TextSolution/Options";
import Answer from "@/components/Answer";
import { IVideoData, IVideoPage } from "./interfaces";
import Playlist from "@/components/Playlist";
import CollapsiblePlaylist from "@/components/Playlist/Collapsible";
import { getCurrentVersionData } from "@/helpers/versionManager";
import Solution from "@/components/TextSolution/Solution";
import { PAGE, PAGES_CACHE, TARGET_GROUP_TYPES } from "@/config/enums";
import { IVideoPlayerData } from "@/helpers/VideoPlayer/interface";
import Pills from "@/components/Pills";
import Heading from "@/components/Badge/Heading";

import { getTextSolution, getVideoBanner } from "@/helpers/versionManager/VideoPage";
import StepsSolver from "@/components/TextSolution/StepsSolver";
import ImageBanner from "@/components/CourseWidget/ImageBanner";
import BreadCrumb from "@/components/Header/Breadcrumb";
import BannerBelowVideo from "@/components/VideoPlayer/BannerBelowVideo";

const SimilarVideoPage = dynamic(() => import("@/components/Similar/SimilarVideoPage"));
const DownloadAppBS = dynamic(() => import("@/components/BottomSheet/DownloadApp"));
const SocialShareModal = dynamic(() => import("@/components/Modal/SocialShare"));
const DislikeFeedbackModal = dynamic(() => import("@/components/Modal/DislikeFeedback"));
const VideoInteractionButtons = dynamic(() => import("@/components/VideoInteraction"));
// const FaComment = dynamic(() => import("@/components/SVG/Fontawesome/FaComment"));


const VideoPagePills = (props: {
  videoData: IVideoData;
}) => {
  const {
    videoData,
  } = props;
  const items: any[] = [
    { id: "class", text: `Class ${videoData.class}`, href: `/class-${videoData.class}`, className: "text-sm !no-underline !rounded-xl", type: "pillShadow" },
  ];

  if (videoData.subject) {
    items.push({ id: "subject", text: videoData.subject, href: `/class-${videoData.class}/${videoData.subject.toLowerCase().split(" ").join("-")}`, className: "text-sm !no-underline !rounded-xl", type: "pillShadow" });
  }
  if (videoData.chapter) {
    items.push({ id: "chapter", text: videoData.chapter, href: `/class-${videoData.class}/${videoData.subject.toLowerCase().split(" ").join("-")}/${videoData.chapter.toLowerCase().split(" ").join("-")}`, className: "text-sm !no-underline !rounded-xl", type: "pillShadow" });
  }

  return <section className="my-0">
    <hr className="my-8 sm:my-12" />
    <Pills items={items} />
  </section>
}

const VideoPage = (props: IVideoPage) => {
  const { language } = props.query;
  const { videoData, textSolutionMathJax, canonicalUrl } = props;
  const breadcrumbs = props.breadcrumbs?.items?.slice(0, props.breadcrumbs?.items?.length - 1) || [];
  const currentText = props.breadcrumbs?.items?.[props.breadcrumbs?.items?.length - 1]?.text;

  const {
    subject: questionSubject, class: studentClass, matched_questions, chapter, total_likes,
  } = videoData;
  const ocrTextClean = cleanOcrText(props.ocrText);

  const languageHeadings = allHeadings[language ?? "default"] ?? allHeadings["default"];

  const subjectName: string = toTitleCase(questionSubject || "");
  const isBio = subjectName?.toLowerCase().includes("bio") || false;
  
  const isEven = (videoData.question_id % 2) === 0;
  const isAnswered = !!props.videoData.is_answered;
  const isTextAnswered = !!props.videoData.is_text_answered;
  const isImageAnswered = !!videoData.answer_image_url;
  const hasOptions = !!(videoData.text_solution.opt_1 && videoData.text_solution.opt_2 && videoData.text_solution.opt_3 && videoData.text_solution.opt_4);

  const { typeOfSolution, typeOfSolutionText } = getSolutionTypeText(isImageAnswered, isTextAnswered, isAnswered);

  let metaDescription = getMetaDescription(isBio, typeOfSolutionText, ocrTextClean || "", subjectName, studentClass, chapter);
  const cleanTextSolution = cleanOcrText(videoData.text_solution.solutions)
  if (cleanTextSolution) {
    metaDescription = `${cleanTextSolution}${(cleanTextSolution.length < 50) ? ocrTextClean : ""}`
  }
  const headTitle = getHeadTitle(ocrTextClean ?? "", videoData.text_solution);
  const nonNativeVideoLanguage = isAnswered ? getLanguageFromNonNativeLocale(videoData.video_locale) : "";

  const nextVideo = videoData.current_playlist.resources?.[videoData.current_playlist.resources?.findIndex(x => x.current) + 1] ?? matched_questions[0];
  if (nextVideo) {
    nextVideo.canonicalUrlRelative = `/qna/${nextVideo.question_id}`;
  }
  const previousVideo = videoData.current_playlist.resources?.[videoData.current_playlist.resources?.findIndex(x => x.current) - 1];
  if (previousVideo) {
    previousVideo.canonicalUrlRelative = `/qna/${previousVideo.question_id}`;
  }

  videoData.current_playlist.resources?.forEach((item) => {
    item.url = `/qna/${item.question_id}`;
  });

  const videoPageHeadProps: IVideoPageHead = {
    canonicalUrl,
    language,
    createdAt: (videoData.answer_creation ? new Date(videoData.answer_creation) : null) || new Date("2020-01-01T00:00:00Z"),
    isAnswered,
    isTextAnswered,
    isImageAnswered,
    answer: {
      solution: videoData.text_solution?.solutions ?? "",
      text: videoData.text_solution?.answer ?? "",
      upvoteCount: props.questionId ? (props.questionId % 1000 + props.questionId % 100) : countUpvotes(total_likes),
      options: {
        opt1: videoData.text_solution.opt_1 ?? "",
        opt2: videoData.text_solution.opt_2 ?? "",
        opt3: videoData.text_solution.opt_3 ?? "",
        opt4: videoData.text_solution.opt_4 ?? "",
      },
    },
    ocrTextClean: ocrTextClean ?? "",
    ocrText: props.ocrText ?? "",
    questionId: props.questionId as number,
    answerId: videoData.answer_id,
    title: headTitle ?? "",
    metaDescription,
    keywords: [
      getMetaKeyword(videoData.class.toString(), "class"),
      getMetaKeyword(videoData.subject),
      getMetaKeyword(`${videoData.book_name} ${videoData.chapter}`),
    ],
    pageLocale: isPureHindi(videoData.student_id) ? "hi" : "en",
    subject: videoData.subject,
    class: videoData.class,
    hasOptions,
    videoLanguage: nonNativeVideoLanguage,
    nextUrl: nextVideo?.canonicalUrlRelative,
    previousUrl: previousVideo?.canonicalUrlRelative,
  };

  const promotionBannerProps = {
    imgUrl: (videoData.class && videoData.target_group && videoData.target_group_type) ?
      `https://d10lpgp6xz60nq.cloudfront.net/engagement_framework/942CFB81-29DC-196B-0FF2-EE144F812E49.webp`
      : `${STATIC_CDN}/web_banners/videopage_banner/default.webp`,
    alt: "Doubtnut Promotions Banner",
    href: `https://allen.onelink.me/90jx/88lahtgd`
  };



  const showTargetGroupType = Number(videoData.class) > 12;
  const targetGroupType = TARGET_GROUP_TYPES[videoData.target_group_type as keyof typeof TARGET_GROUP_TYPES] ?? "Boards";

  return (
    <>
      <div id="webpage">
        <VideoPageHead {...videoPageHeadProps} />
        <DownloadAppBS classType={showTargetGroupType ? targetGroupType : Number(videoData.class)} />
        <div id="article" className="body-content mt-8 flex flex-col sm:flex-row">
          <div id="focus-container" className="flex items-center flex-col basis-3/4 grow" >
            <div id="abf-container" className="flex flex-wrap justify-center w-full">
              <div id="qa-container" className="flex flex-col items-center grow">
                {
                  props.breadcrumbs && currentText && <BreadCrumb breadcrumbs={breadcrumbs} currentText={currentText} className={`${props.breadcrumbs?.className} self-start !pt-0`} />
                }
                {
                  props.ocrTextMathJax &&
                  <div id="speakableSection" className="relative w-full overflow-x-auto flex flex-col overflow-y-hidden">
                    <section className="my-1">
                      <h1 id="ocr-text" className="text-base lg:text-lg mt-2 mb-0 font-semibold">
                        <MathJax ocrText={props.ocrTextMathJax} className="!w-full inline-block max-w-[calc(100vw-1rem)] overflow-x-auto" />
                        {
                          nonNativeVideoLanguage && <span className="text-base float-right">Solution in {nonNativeVideoLanguage}</span>
                        }
                      </h1>
                      {
                        hasOptions &&
                        <Options
                          headingText={languageHeadings["text_solution"]}
                          textSolutionMathJax={textSolutionMathJax}
                          questionId={videoData.question_id}
                        />
                      }
                      {/* {
                        (!isAnswered && !isTextAnswered) &&
                        <ImageBanner id="videopage-promotion-banner" data={promotionBannerProps} />
                      } */}
                    </section>
                    {
                      isAnswered &&
                      <section>
                        <div className="card rounded-2xl rounded-b-none !pl-5 !my-1 !mb-0">
                          <Heading headingText={languageHeadings["videoSolution"]} />
                        </div>
                        <div className={`w-full flex justify-start `}>
                          <VideoPlayer className={`lg:card !rounded-t-none ${props.videoData.video_banner ? "lg:!rounded-b-none" : "lg:rounded-2xl"} !p-0`}
                            videoData={videoData as unknown as IVideoPlayerData}
                            videoTitle={ocrTextClean}
                            previousVideo={previousVideo}
                            nextVideo={nextVideo}
                            query={props.query as any}
                            adLocale={isHindi(videoData.student_id) ? "hi" : "en"}
                            alternateVideoLanguages={props.alternateVideoLanguages}
                            page={PAGE.VIDEO_PAGE}
                            fetchResource
                            poster={`${STATIC_CDN}/ss/web/${videoData.answer_id}.webp`}
                          />
                        </div>
                        {
                          props.videoData.video_banner ?
                            <BannerBelowVideo {...props.videoData.video_banner} />
                            :
                            <CollapsiblePlaylist id={videoData.current_playlist.id} subject={videoData.subject} book={videoData.current_playlist.book || videoData.book_name} chapter={videoData.current_playlist.chapter} exercise={videoData.current_playlist.exercise} resources={videoData.current_playlist.resources} />
                        }
                      </section>
                    }
                  </div>
                }
                
                {/* {
                video_name && <div className="hidden sm:block w-full relative">
                  <Socials className="absolute -ml-12 mt-20" />
                </div>
              } */}
                {
                  (isAnswered && !props.videoData.video_banner) && <section>
                    <ImageBanner className="!my-0 sm:!my-4" imageClassName="rounded-2xl" id="videopage-promotion-banner" data={promotionBannerProps} />
                  </section>
                }

                {/* {videoData.stepsSolution && <StepsSolver assumption={videoData.stepsSolution.assumption} steps={videoData.stepsSolution.steps} />} */}

                <Solution
                  aiGenerated={videoData.aiGeneratedSolution}
                  headingText={languageHeadings["text_solution"]}
                  textSolutionMathJax={textSolutionMathJax}
                  onlyText={!isAnswered}
                  questionId={videoData.question_id}
                  updatedAt={videoData?.updatedAt}
                />
                {
                  !isAnswered && <section>
                    <ImageBanner className="!my-0 sm:!my-4" imageClassName="rounded-2xl" id="videopage-promotion-banner" data={promotionBannerProps} />
                  </section>
                }

                {/* User Activity buttons */}
                <section className="!my-1 max-w-screen">
                  <VideoInteractionButtons questionId={videoData.question_id} ocrText={headTitle} ocrTextClean={ocrTextClean} canonicalUrl={canonicalUrl} />
                </section>

                {/* <div className="flex mt-2 lg:mt-4">
                <div className="flex justify-between w-full bg-white dark:bg-slate-700 p-4 rounded">
                  <span className="basis-1/2 font-normal text-sm sm:text-base">Was this answer helpful?</span>
                  <span className="text-gray-600 dark:text-neutral-300 inline-flex space-x-4 text-sm sm:text-base items-center font-medium cursor-pointer">
                    <span className="basis-1/2 flex items-center" onClick={() => !(like || dislike) ? setLike(1) : null}>
                      <FaThumbsUp className="grow pr-2 text-green-500" />
                      <span className="grow text-center">{100 + like}</span>
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="flex items-center" onClick={() => !(like || dislike) ? setDislike(1) : null}>
                      <FaThumbsDown className="grow pr-2 text-red-500" />
                      <span className="grow text-center">{ 50 + dislike}</span>
                    </span>
                  </span>
                </div>
              </div> */}
                {
                  (isAnswered && !textSolutionMathJax?.solution) &&
                  <Answer
                    headingText={languageHeadings["answer"]}
                    typeOfSolution={typeOfSolution}
                    subjectName={subjectName}
                    questionClass={studentClass}
                    ocrText={props.ocrText}
                    updatedAt={videoData?.updatedAt}
                  />
                }
              </div>
            </div>
            <div id="btf-container" className="flex flex-col w-full">
              {/* TODO: removing this and adding it to text solution, however only video solutions won't have this */}
              {/* {
                updatedOn &&
                <span className="w-full tracking-wide font-normal text-xs px-2 my-2 rounded flex items-center space-x-1 justify-end">
                  <span>Updated on:</span>
                  <span>{updatedOn}</span>
                </span>
              } */}
              <VideoPagePills videoData={videoData} />
              <SimilarVideoPage
                isEven={isEven}
                languageHeadings={languageHeadings}
                book={videoData.book_name}
                matchedQuestions={videoData.matched_questions ?? []}
                matchedTextQuestions={videoData.matched_text_questions ?? []}
                similarMathJax={props.similarMathJax ?? []}
                similarMathJaxText={props.similarMathJaxText ?? []}
              />
            </div>
          </div>
          <div id="playlist-container" className="flex-col grow hidden lg:flex basis-1/4 pl-8 lg:max-w-sm space-y-4">
            <Playlist data={{
              header: true,
              book: videoData.current_playlist.book || videoData.book_name,
              chapter: videoData.current_playlist.chapter,
              exercise: videoData.current_playlist.exercise,
              resources: videoData.current_playlist.resources,
              id: videoData.current_playlist.id,
              subject: videoData.subject,
            }} />
          </div>
        </div>
      </div>

      <DislikeFeedbackModal questionId={videoData.question_id} />
      <SocialShareModal title={ocrTextClean} url={canonicalUrl} />
    </>
  );
}


export const getServerSideProps: GetServerSideProps = async (ctx) => {
  // ctx.res.setHeader(
  //   "Cache-Control",
  //   "public, s-maxage=600, stale-while-revalidate=599"
  // );

  // disable not default locale
  if (ctx.locale !== ctx.defaultLocale) {
    return {
      notFound: true,
    }
  }

  const { videoId } = ctx.query;
  if (!videoId) {
    return {
      notFound: true,
    }
  }
  const videoIdSplit = (videoId as string).split("-");
  const props: IVideoPage = {
    questionId: +videoIdSplit[videoIdSplit.length - 1],
    videoData: {} as any,
    query: ctx.query as any,
    similarMathJax: [],
    similarMathJaxText: [],
    deeplink: DEEPLINK.APP_DEFAULT,
    canonicalUrl: "",
  };

  let pageProps = await getCurrentVersionData(PAGES_CACHE.VIDEOPAGE, props);

  if (!pageProps || !pageProps.videoData.ocr_text?.trim() || !(pageProps.videoData.is_answered || pageProps.videoData.is_text_answered) || getExcludedStudentIds().includes(pageProps.videoData.student_id)) {
    return {
      notFound: true,
    };
  }

  const canonicalUrlRelative = `/qna/${pageProps.videoData.question_id}`;
  pageProps.canonicalUrl = `${FRONTEND_HOST}${canonicalUrlRelative}`;

  if (!isCSRRequest(ctx.req.url) && ctx.req.url !== canonicalUrlRelative && !ctx.req.url?.startsWith("/whatsapp/qa/")) {
    return {
      redirect: {
        basePath: false,
        destination: canonicalUrlRelative,
        permanent: true,
      },
    };
  }

  pageProps = await getTextSolution(pageProps);
  pageProps = await getVideoBanner(pageProps);

  pageProps.videoData.matched_text_questions?.slice(3).forEach((item: any) => delete item.text_solution);

  // TODO use exercise as well
  const breadcrumbs = [{
    text: `Class ${pageProps.videoData.class}`,
    url: `/class-${pageProps.videoData.class}`,
  }, {
    text: pageProps.videoData.subject,
    url: `/class-${pageProps.videoData.class}/${pageProps.videoData.subject.toLowerCase().split(" ").join("-")}`,
  }, {
    //   text: pageProps.videoData.book_name,
    //   url: `/class-${pageProps.videoData.class}/${pageProps.videoData.subject.toLowerCase().split(" ").join("-")}/${pageProps.videoData.book_name.toLowerCase().split(" ").join("-")}`,
    // }, {
    //   text: pageProps.videoData.chapter, // TODO use chapterAlias
    //   url: `/class-${pageProps.videoData.class}/${pageProps.videoData.subject.toLowerCase().split(" ").join("-")}/${pageProps.videoData.chapter.toLowerCase().split(" ").join("-")}`,
    // }, {
    text: pageProps.videoData.ocr_text,
    url: canonicalUrlRelative,
  }];

  return {
    props: {
      ...pageProps,
      breadcrumbs: { items: breadcrumbs },
    },
  }
}

export default VideoPage;

